import { themes } from 'kb-shared';
import { TabNavigator } from 'kb-web-shared';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-apollo';
import { connect } from 'react-redux';

import Loader from '../../components/Loader/Loader';
import { AbsoluteCentered } from '../../styled';
import { showErrorToast } from '../../utilities/notificationUtils';
import DocumentsList from './components/DocumentsList';
import AttachmentsList from './components/AttachmentsList';
import { YourUploads } from './components/YourUploads';
import { getDocumentsAsync } from './lib/DocumentManager';
import documentText from './text.json';
import { Container, Title, DescriptionText } from './styled';

import {
  kindbodyDocumentsQuery,
  KindbodyDocumentsResponse
} from './graphql/kindbodyDocumentsQuery';
import { AllAttachmentsQuery, AllAttachmentResponse } from './graphql/attachementsQuery';

type DocumentCard = {
  key: string;
  title: string;
};

const items: {
  [key: string]: DocumentCard;
} = {
  kindbodyDocs: {
    key: 'kindbodyDocs',
    title: 'Kindbody Lab Results'
  },
  kindbodyAttachments: {
    key: 'kindbodyAttachments',
    title: 'Kindbody Uploads'
  },
  personalDocs: {
    key: 'personalDocs',
    title: 'Your Uploads'
  }
};

const Documents = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [kindbodyDocs, setKindbodyDocs] = useState([]);
  const [documents, setDocuments] = useState(null);

  const { data, loading: documentsLoading } = useQuery<KindbodyDocumentsResponse>(
    kindbodyDocumentsQuery
  );
  const {
    data: attachmentInformation,
    refetch: refetchAttachments,
    loading: attachmentsLoading
  } = useQuery<AllAttachmentResponse>(AllAttachmentsQuery, { fetchPolicy: 'no-cache' });

  // @ts-ignore
  const labOrderReports = data?.labOrderReports;

  const getDocuments = useCallback(() => {
    getDocumentsAsync()
      .then(documents => {
        // @ts-ignore
        setDocuments(documents);
      })
      .catch(() => {
        showErrorToast("We're sorry, something went wrong");
      });
  }, []);

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Documents');
    }
  }, []);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  useEffect(() => {
    if (!labOrderReports) return;

    // @ts-ignore
    const mappedDocs = labOrderReports.reduce((acc, labOrderReport) => {
      const pdfResults = labOrderReport.pdfResults.map(v => {
        const date = moment.utc(labOrderReport.createdAt).format('MM/DD/YYYY');
        return { path: v, type: 'PDF', date, fileName: `Lab results ${date}` };
      });

      const htmlResults = labOrderReport.htmlResults.map(v => {
        const date = moment.utc(labOrderReport.createdAt).format('MM/DD/YYYY');
        return { path: v, type: 'HTML', date, fileName: `Lab results ${date}` };
      });
      return [...acc, ...pdfResults, ...htmlResults];
    }, []);
    // @ts-ignore
    setKindbodyDocs(mappedDocs);
  }, [labOrderReports]);

  if (!documents || documentsLoading || attachmentsLoading) {
    return (
      <AbsoluteCentered>
        <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
      </AbsoluteCentered>
    );
  }

  const itemsToDisplay = [];
  // @ts-ignore
  if (kindbodyDocs && kindbodyDocs.length > 0) {
    itemsToDisplay.push(items.kindbodyDocs);
  }

  if (attachmentInformation?.allKindbodyAttachments) {
    itemsToDisplay.push(items.kindbodyAttachments);
  }

  itemsToDisplay.push(items.personalDocs);

  const renderTabContent = (index: number, item) => {
    switch (item.key) {
      case 'kindbodyDocs':
        return (
          <DocumentsList
            // @ts-ignore
            items={kindbodyDocs}
            properties={['date', 'type']}
            hasHeader
          />
        );
      case 'kindbodyAttachments':
        return (
          <AttachmentsList
            items={attachmentInformation?.allKindbodyAttachments || []}
            hasHeader
            properties={['date']}
          />
        );
      case 'personalDocs':
        return (
          <YourUploads
            // @ts-ignore
            items={attachmentInformation?.allPatientAttachments || []} //documents.personalDocs}
            onUpload={success => {
              if (!success) {
                // Honeybadger is taken care of in DocumentManager
                return;
              }
              getDocuments();
              refetchAttachments();
            }}
            onDelete={() => {
              getDocuments();
              refetchAttachments();
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Title>Documents</Title>
      <DescriptionText>{documentText.descriptionText}</DescriptionText>
      <TabNavigator
        currentIndex={tabIndex}
        getSectionTitle={(index, item) => item.title}
        getUniqueKey={(index, item) => `document-tab-nav-${item.key}`}
        items={itemsToDisplay}
        onSelect={index => setTabIndex(index)}
        renderContent={renderTabContent}
      />
    </Container>
  );
};

export default connect()(Documents);
